/**
 * DraftKeeper — the safety net that makes "nothing you type is lost" true
 * across the WHOLE app without touching each of the 87 screens.
 *
 * It works by delegation: it listens for typing on the document, derives a
 * stable key for the field, and saves the value locally (debounced). When a
 * screen mounts again it puts the text back — but only into fields that are
 * still empty, so it never clobbers what the user is writing now or what the
 * app just loaded from the server.
 *
 * Why delegation instead of a hook per form: the app has dozens of forms
 * written over months, several with their own state handling. A hook would
 * have to be wired into every one of them (and into every future one, which
 * someone WILL forget). This covers them all, including the ones nobody
 * remembers to update.
 *
 * Opt out of an individual field or subtree with `data-no-draft`.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Snackbar, Button } from '@mui/material';
import { saveDraft, readDraft, sweepExpired, clearViewDrafts } from '../../services/drafts';

const SAVE_DEBOUNCE_MS = 400;

/** Fields we must never persist, no matter what. */
function isExcluded(el) {
  if (!el || el.disabled) return true;
  const tag = el.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return false;
  if (tag === 'INPUT') {
    const t = (el.type || 'text').toLowerCase();
    // Passwords never touch disk. File/checkbox/radio aren't text to restore.
    if (['password', 'hidden', 'file', 'checkbox', 'radio', 'submit', 'button', 'image', 'reset'].includes(t)) return true;
  }
  if (el.closest('[data-no-draft]')) return true;
  if ((el.getAttribute('autocomplete') || '').toLowerCase() === 'one-time-code') return true;
  return false;
}

function isTextField(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return false;
  return !isExcluded(el);
}

/**
 * A key that survives remounts. Falls back down the chain because this app's
 * inputs are inconsistent: some have name, some only a placeholder.
 */
function fieldKey(el) {
  const direct = el.name || el.id || el.getAttribute('aria-label') || el.getAttribute('placeholder');
  if (direct) return `f:${direct}`.slice(0, 120);
  // Last resort: position among its siblings of the same tag inside its form.
  const form = el.closest('form') || el.closest('[role="dialog"]') || document.body;
  const same = Array.from(form.querySelectorAll(el.tagName.toLowerCase()));
  return `p:${el.tagName}:${same.indexOf(el)}`;
}

/** Write into a React-controlled input so React itself sees the change. */
function setControlledValue(el, value) {
  const proto = el.tagName === 'TEXTAREA'
    ? window.HTMLTextAreaElement.prototype
    : el.tagName === 'SELECT'
      ? window.HTMLSelectElement.prototype
      : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value); else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

const DraftKeeper = ({ scope: view }) => {
  const timers = useRef(new Map());
  const restored = useRef(new Set());
  const [recovered, setRecovered] = useState(0);

  // Housekeeping once per session: drop anything past its expiry.
  useEffect(() => { sweepExpired(); }, []);

  // ── Save on type ────────────────────────────────────────────────────────
  useEffect(() => {
    const map = timers.current;
    const onInput = (e) => {
      const el = e.target;
      if (!isTextField(el)) return;
      const key = fieldKey(el);
      clearTimeout(map.get(key));
      map.set(key, setTimeout(() => {
        saveDraft(view, key, el.value);
        map.delete(key);
      }, SAVE_DEBOUNCE_MS));
    };

    // Flush pending saves if the phone backgrounds the tab or the user leaves:
    // a debounce that never fires is a draft that never existed.
    const flush = () => {
      document.querySelectorAll('input, textarea, select').forEach((el) => {
        if (isTextField(el) && el.value) saveDraft(view, fieldKey(el), el.value);
      });
    };
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };

    document.addEventListener('input', onInput, true);
    document.addEventListener('change', onInput, true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      flush();
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('change', onInput, true);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, [view]);

  // ── Restore when the screen (re)appears ─────────────────────────────────
  useEffect(() => {
    restored.current = new Set();
    setRecovered(0);

    const restoreIn = (root) => {
      const nodes = root.querySelectorAll ? root.querySelectorAll('input, textarea, select') : [];
      let n = 0;
      nodes.forEach((el) => {
        if (!isTextField(el)) return;
        const key = fieldKey(el);
        if (restored.current.has(key)) return;
        // Never overwrite: only fill what is still empty.
        if (el.value) { restored.current.add(key); return; }
        const saved = readDraft(view, key);
        if (saved === undefined || saved === '') return;
        restored.current.add(key);
        setControlledValue(el, saved);
        n += 1;
      });
      // Texto que el usuario no acaba de escribir necesita explicación, o
      // parece que la app se inventó datos.
      if (n) setRecovered((prev) => prev + n);
    };

    // Forms mount async (data loads, dialogs open), so watch for late arrivals.
    const t0 = setTimeout(() => restoreIn(document), 60);
    const obs = new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes.forEach((n) => {
        if (n.nodeType === 1) restoreIn(n);
      }));
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { clearTimeout(t0); obs.disconnect(); };
  }, [view]);

  const discard = useCallback(() => {
    clearViewDrafts(view);
    document.querySelectorAll('input, textarea, select').forEach((el) => {
      if (isTextField(el) && restored.current.has(fieldKey(el))) setControlledValue(el, '');
    });
    setRecovered(0);
  }, [view]);

  return (
    <Snackbar
      open={recovered > 0}
      autoHideDuration={7000}
      onClose={() => setRecovered(0)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      message="Recuperamos lo que habías escrito"
      action={
        <Button size="small" onClick={discard} sx={{ color: '#E0892E', fontWeight: 700 }}>
          Descartar
        </Button>
      }
      // Encima de la barra inferior de navegación en el teléfono.
      sx={{ bottom: { xs: 'calc(74px + env(safe-area-inset-bottom)) !important', md: 24 } }}
    />
  );
};

export default DraftKeeper;
